import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { conflictingTripsFrom } from '../policies/booking-labels';
import {
  cancelWorkshopBooking,
  createWorkshopBooking,
  moveWorkshopBooking,
  type BookingTripConflict,
  type CreateWorkshopBookingBody,
  type MoveWorkshopBookingBody,
  type WorkshopBookingView,
} from '../workshop-bookings.api';

/** `conflicts` is a booking the server refused because it runs into committed trips: the form shows them and offers to book anyway. */
export type BookingOutcome = { status: 'saved' } | { status: 'failed' } | { status: 'conflicts'; trips: BookingTripConflict[] };

/**
 * Creating, moving and cancelling workshop bookings. A failure stays inline (`error`); a booking the server confirms toasts, past tense,
 * naming the truck and workshop. `onChanged` re-reads whatever list the caller is showing.
 */
export function useBookingActions(onChanged: () => Promise<void> | void) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<BookingTripConflict[] | null>(null);
  const [saving, setSaving] = useState(false);

  async function run(action: () => Promise<WorkshopBookingView>, describe: (booking: WorkshopBookingView) => string): Promise<BookingOutcome> {
    setError(null);
    setSaving(true);
    try {
      const booking = await action();
      await onChanged();
      toast.success(describe(booking));
      setConflicts(null);
      return { status: 'saved' };
    } catch (err) {
      const trips = conflictingTripsFrom(err);
      if (trips) {
        setConflicts(trips);
        return { status: 'conflicts', trips };
      }
      setError(apiErrorMessage(err) ?? 'Could not save — check the fields and try again.');
      return { status: 'failed' };
    } finally {
      setSaving(false);
    }
  }

  return {
    error,
    conflicts,
    saving,
    resetFeedback: () => {
      setError(null);
      setConflicts(null);
    },
    create: (body: CreateWorkshopBookingBody) =>
      run(
        () => createWorkshopBooking(body),
        (booking) => `${booking.truckRegistration} booked into ${booking.workshopName}`,
      ),
    move: (id: string, body: MoveWorkshopBookingBody) =>
      run(
        () => moveWorkshopBooking(id, body),
        (booking) => `${booking.truckRegistration}'s booking at ${booking.workshopName} moved`,
      ),
    cancel: (id: string, reason: string) =>
      run(
        () => cancelWorkshopBooking(id, { reason }),
        (booking) => `${booking.truckRegistration}'s booking at ${booking.workshopName} cancelled`,
      ),
  };
}
